import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import MarkerPopupTemplate from './MarkerPopupTemplate';

export default class MapBox {
  constructor(data, translation, disableScrollZoom, initialCameraSettings) {
    this.translation = translation;
    this.disableScrollZoom = disableScrollZoom;
    this.data = data;
    this.map = null;
    this.mapbox = document.createElement('div');
    this.mapbox.classList.add('ac-map__map-box');
    this.mapbox.setAttribute('id', 'ac-map__map-box-container--modal');
    this.defaultSettings = initialCameraSettings || {
      center: [67.286524787259737, 52.65254798410314],
      zoom: 2.5,
    };

    mapboxgl.accessToken = 'pk.eyJ1Ijoid2FsZGJlZWsiLCJhIjoiY2txZGtoM3R4MXVidDJwbXZleHdzMnRwayJ9.E7oonT0TTTOtC9mbyyPC_A';
  }

  reset() {
    const { center, zoom } = this.defaultSettings;

    this.map.setCenter(center);
    this.map.setZoom(zoom);
  }

  init(onLoadCallback) {
    const { center, zoom } = this.defaultSettings;
    const map = new mapboxgl.Map({
      container: 'ac-map__map-box-container--modal',
      style: 'mapbox://styles/waldbeek/cl5l4msi1000q14q9kz3pvx06',
      fadeDuration: 0,
      pitch: 40,
      antialias: true,
      center,
      zoom,
    });

    map.addControl(new mapboxgl.NavigationControl());

    if (this.disableScrollZoom) {
      map.scrollZoom.disable();
    }

    map.on('load', () => {
      map.addSource('places', {
        type: 'geojson',
        data: this.data.source.data,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 40,
      });

      this.clustersManager(map);

      this.handlePopupClick(map);
      this.handlePopupHover(map);

      this.map = map;

      if (onLoadCallback) {
        onLoadCallback();
      }
    });
  }

  handlePopupClick(map) {
    const { translation } = this;

    map.on('click', 'unclustered-point', function(e) {
      let coordinates = e.features[0].geometry.coordinates.slice();
      const template = MarkerPopupTemplate(e.features[0].properties, translation);

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      if (window.screen.availWidth > 767) {
        new mapboxgl.Popup({ offset: 25 })
          .setLngLat(coordinates)
          .setHTML(template)
          .setMaxWidth('400px')
          .addTo(map);
      } else {
        const mapPopup = document.querySelector('.ac-map__popup');
        const popupContent = document.createElement('div');

        window.onresize = function() {
          if (window.screen.availWidth > 767) {
            mapPopup.childNodes.forEach(element => {
              if (element.classList.contains('mapboxgl-popup-content--bottom')) {
                mapPopup.removeChild(element);
              }
            });
          }
        };

        mapPopup.childNodes.forEach(element => {
          if (element.classList.contains('mapboxgl-popup-content--bottom')) {
            mapPopup.removeChild(element);
          }
        });

        popupContent.innerHTML = `<div class="mapboxgl-popup-content--bottom-wrap">${template}</div>`;
        popupContent.classList.add('mapboxgl-popup-content--bottom');

        mapPopup.appendChild(popupContent);

        const popupContentWrap = document.querySelector('.mapboxgl-popup-content--bottom-wrap');
        const closeButtonWrap = document.createElement('button');

        closeButtonWrap.classList.add('ac-map__close', 'ac-map__close--bottom');
        closeButtonWrap.innerText = '';

        mapPopup.addEventListener('click', function(event) {
          const target = event.target;

          if (target.classList.contains('ac-map__close--bottom')) {
            const mapPopup = document.querySelector('.ac-map__popup');

            mapPopup.removeChild(popupContent);
          }
        });
        popupContentWrap.appendChild(closeButtonWrap);
      }
    });
  }

  handlePopupHover(map) {
    map.on('mouseenter', 'unclustered-point', function(e) {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'unclustered-point', function() {
      map.getCanvas().style.cursor = '';
    });
  }

  clustersManager(map) {
    let hoveredStateId = null;

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'places',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          'rgba(0, 0, 0, 0)',
          100,
          'rgba(0, 0, 0, 0)',
          750,
          'rgba(0, 0, 0, 0)',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
      },
    });

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'places',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'icon-image': 'empty--hover',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    map.addLayer({
      id: 'cluster-count--default',
      type: 'symbol',
      source: 'places',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'icon-image': 'empty',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'places',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': '{icon}--hover',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
    });

    map.addLayer({
      id: 'unclustered-point--default',
      type: 'symbol',
      source: 'places',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': '{icon}',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
    });

    map.on('click', 'clusters', function(e) {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = features[0].properties.cluster_id;

      map.getSource('places').getClusterExpansionZoom(clusterId, function(err, zoom) {
        if (err) return;

        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        });
      });
    });

    map.on('mouseenter', 'cluster-count--default', function(e) {
      const id = e.features[0].id;

      map.setFilter('cluster-count--default', ['!=', 'cluster_id', id]);

      hoveredStateId = id;
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'cluster-count', function() {
      map.getCanvas().style.cursor = '';

      if (hoveredStateId) {
        map.setFilter('cluster-count--default', ['!=', 'cluster_id', 0]);
        hoveredStateId = null;
      }
    });

    map.on('mouseenter', 'unclustered-point--default', e => {
      const id = e.features[0].id;

      map.setFilter('unclustered-point--default', ['!=', 'id', id]);

      hoveredStateId = id;
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'unclustered-point', () => {
      if (hoveredStateId) {
        map.setFilter('unclustered-point--default', ['!=', 'id', 0]);
        hoveredStateId = null;
      }

      map.getCanvas().style.cursor = '';
    });
  }

  setData(data) {
    this.map.getSource('places').setData({
      type: 'FeatureCollection',
      features: data,
    });

    this.reset();
  }

  getElement() {
    return this.mapbox;
  }
}
